import {
  DEFAULT_MEMBER_DASHBOARD_LAYOUT,
  MEMBER_DASHBOARD_LAYOUT_VERSION,
} from './memberDashboardLayoutConfig';

const STORAGE_PREFIX = `member-dashboard-layout:${MEMBER_DASHBOARD_LAYOUT_VERSION}`;

/**
 * Layout scope: own dashboard uses owner userId only.
 * View-as uses a separate key per subject so the admin grid does not overwrite
 * their personal layout or the subject's own browser layout key.
 */
export function getMemberDashboardLayoutStorageKey({
  userId,
  isViewAsActive = false,
  viewAsUserId = null,
} = {}) {
  if (userId == null || userId === '') return null;

  if (isViewAsActive && viewAsUserId != null && viewAsUserId !== '') {
    return `${STORAGE_PREFIX}:${userId}:view-as:${viewAsUserId}`;
  }

  return `${STORAGE_PREFIX}:${userId}`;
}

function migrateSavedLayoutKeys(saved) {
  if (!Array.isArray(saved)) return saved;
  const hasHistory = saved.some((item) => item?.i === 'history');
  if (hasHistory) return saved;
  return saved.map((item) => (item?.i === 'incomes' ? { ...item, i: 'history' } : item));
}

export function mergeMemberDashboardLayout(saved) {
  const defaultsByKey = Object.fromEntries(
    DEFAULT_MEMBER_DASHBOARD_LAYOUT.map((item) => [item.i, { ...item }])
  );

  const migrated = migrateSavedLayoutKeys(saved);

  if (Array.isArray(migrated)) {
    migrated.forEach((item) => {
      if (!item?.i || !defaultsByKey[item.i]) return;
      defaultsByKey[item.i] = {
        ...defaultsByKey[item.i],
        x: Number(item.x),
        y: Number(item.y),
        w: Number(item.w),
        h: Number(item.h),
        minW: item.minW ?? defaultsByKey[item.i].minW,
        minH: item.minH ?? defaultsByKey[item.i].minH,
      };
    });
  }

  return DEFAULT_MEMBER_DASHBOARD_LAYOUT.map((item) => defaultsByKey[item.i]);
}

function isValidSavedLayout(saved) {
  if (!Array.isArray(saved) || saved.length === 0) return false;

  const migrated = migrateSavedLayoutKeys(saved);
  const expectedKeys = new Set(DEFAULT_MEMBER_DASHBOARD_LAYOUT.map((item) => item.i));
  const seen = new Set();

  return migrated.every((item) => {
    if (!item?.i || !expectedKeys.has(item.i) || seen.has(item.i)) return false;
    seen.add(item.i);
    return (
      Number.isFinite(Number(item.w)) &&
      Number.isFinite(Number(item.h)) &&
      Number(item.w) > 0 &&
      Number(item.h) > 0
    );
  });
}

export function loadMemberDashboardLayout(layoutScope) {
  const key = getMemberDashboardLayoutStorageKey(layoutScope);
  if (!key || typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = migrateSavedLayoutKeys(JSON.parse(raw));
    if (!isValidSavedLayout(parsed)) return null;
    return mergeMemberDashboardLayout(parsed);
  } catch (error) {
    console.warn('Failed to load member dashboard layout:', error);
    return null;
  }
}

export function clearMemberDashboardLayout(layoutScope) {
  const key = getMemberDashboardLayoutStorageKey(layoutScope);
  if (!key || typeof window === 'undefined') return;
  window.localStorage.removeItem(key);
}

export function hasSavedMemberDashboardLayout(layoutScope) {
  const key = getMemberDashboardLayoutStorageKey(layoutScope);
  if (!key || typeof window === 'undefined') return false;

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return false;
    const parsed = migrateSavedLayoutKeys(JSON.parse(raw));
    return isValidSavedLayout(parsed);
  } catch {
    return false;
  }
}

export function saveMemberDashboardLayout(layoutScope, layout) {
  const key = getMemberDashboardLayoutStorageKey(layoutScope);
  if (!key || !Array.isArray(layout) || typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(key, JSON.stringify(layout));
  } catch (error) {
    console.warn('Failed to save member dashboard layout:', error);
  }
}
